import React from 'react';
import { MoreHorizontal } from 'lucide-react';
import { requestRowBadge } from '../../utils/requestKinds';
import InlineNameEditor from '../ui/InlineNameEditor';
import { SIDEBAR_TREE_ROW_ACTIVE, SIDEBAR_TREE_ROW_IDLE } from './sidebarState';

// ─── RequestRow ───────────────────────────────────────────────────────────────

export const RequestRow = React.memo(function RequestRow({
  request,
  isMarked = false,
  onRowClick,
  activeDragId,
  selectedRequest,
  onSelectRequest,
  onContextMenu,
  onRequestDelete,
  disableDnd,
  editingItem,
  onSaveRename,
  onRenameDraft,
  onCancelRename,
  indent = 0,
  dropIndicator,
  getHandleProps,
  fadeInIds,
}) {
  const canEdit    = request.can_edit !== false;
  const dndDisabled = disableDnd || !canEdit;
  const isDragging = activeDragId === `request-${request.id}`;
  const isEditing  =
    editingItem?.type === 'request' && String(editingItem.id) === String(request.id);

  const ind = dropIndicator?.overId === `request-${request.id}` ? dropIndicator : null;
  const dropBefore = ind?.position === 'before';
  const dropAfter  = ind?.position === 'after';
  const badge = requestRowBadge(request); // the verb — or WS / SSE / gRPC

  return (
    <div
      className={`sidebar-row-wrap${
        fadeInIds?.has(String(request.id)) ? ' animate-fadeIn' : ''
      }`}
    >
      {dropBefore && <div className="sidebar-drop-line" aria-hidden />}
      <div
        data-drag-id={`request-${request.id}`}
        style={{ opacity: isDragging ? 0.4 : undefined }}
        className={`flex items-center px-2 py-1 rounded-md group transition-all duration-200 cursor-pointer ${
          selectedRequest?.id === request.id || isMarked
            ? SIDEBAR_TREE_ROW_ACTIVE
            : SIDEBAR_TREE_ROW_IDLE
        }`}
        role="treeitem"
        aria-selected={isMarked}
        onClick={(e) => {
          if (onRowClick?.(e, `request-${request.id}`)) return;
          onSelectRequest(request);
        }}
        onContextMenu={(e) => onContextMenu(e, 'request', request)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectRequest(request); return; }
          // Delete/Backspace opens the SAME confirmation the context menu
          // uses, rather than deleting outright: these rows hold hours of
          // work, and the two most overloaded keys on the keyboard are not
          // where an irreversible action belongs. Refused while renaming, so
          // the key stays with the text field.
          if ((e.key === 'Delete' || e.key === 'Backspace') && canEdit && !isEditing) {
            e.preventDefault();
            onRequestDelete?.('request', request);
          }
        }}
        {...(!dndDisabled && !isEditing ? getHandleProps(`request-${request.id}`) : {})}
      >
        <span
          className={`sidebar-request-method shrink-0 font-semibold uppercase tracking-wide mr-1 pointer-events-none ${badge.className}`}
        >
          {badge.label}
        </span>
        {isEditing ? (
          <InlineNameEditor
            value={request.name}
            className="text-xs font-normal"
            onSave={(name) => onSaveRename?.('request', request.id, name)}
            onDraftChange={(draft) => onRenameDraft?.('request', request.id, draft)}
            onCancel={() => onCancelRename?.()}
          />
        ) : (
          <span className="flex-1 min-w-0 text-xs text-gray-800 dark:text-zinc-200 truncate pointer-events-none">
            {request.name}
          </span>
        )}
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onContextMenu(e, 'request', request); }}
          className="opacity-0 group-hover:opacity-100 shrink-0 p-1 bg-transparent hover:bg-gray-200 dark:hover:bg-zinc-700 rounded transition-colors duration-150 cursor-pointer"
        >
          <MoreHorizontal size={12} className="text-gray-500 dark:text-zinc-400" />
        </button>
      </div>
      {dropAfter && <div className="sidebar-drop-line" aria-hidden />}
    </div>
  );
});
